import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaMapMarkerAlt, FaArrowLeft, FaImages, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import api from '../../api/axiosConfig';
import './event-details.css';

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (id && id !== 'undefined') {
      fetchEventDetails();
    } else {
      setError('Invalid event ID');
      setLoading(false);
    }
  }, [id]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      setError('');

      const postsResponse = await api.get('/posts', {
        params: { status: 1 }
      });

      if (postsResponse.data.success) {
        const posts = postsResponse.data.data || [];
        const eventData = posts.find(post => post.id === id);

        if (!eventData) {
          throw new Error('Event not found');
        }

        const picturesResponse = await api.get('/pictures', {
          params: { post_id: eventData.id }
        });

        let images = [];
        if (picturesResponse.data.success) {
          images = picturesResponse.data.data.filter(image => 
            image.post_id === eventData.id
          );
        }

        const eventWithPictures = {
          ...eventData,
          images: images
        };

        setEvent(eventWithPictures);
        
        if (eventData.category) {
          fetchRelatedEvents(eventData.category, eventData.id);
        }

      } else {
        throw new Error('Failed to load events');
      }

    } catch (err) {
      if (err.response?.status === 404) {
        setError('Event not found. The event may have been removed.');
      } else if (err.message.includes('Network Error')) {
        setError('Cannot connect to the server. Please check your connection.');
      } else {
        setError('Unable to load event details. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedEvents = async (category, currentEventId) => {
    try {
      const response = await api.get('/posts', {
        params: { status: 1 }
      });

      if (response.data.success && response.data.data) {
        const relatedPosts = response.data.data
          .filter(post => post.category === category && post.id !== currentEventId)
          .slice(0, 3);

        const relatedWithPictures = await Promise.all(
          relatedPosts.map(async (post) => {
            try {
              const picturesResponse = await api.get('/pictures', {
                params: { post_id: post.id }
              });

              return {
                ...post,
                images: picturesResponse.data.success ? picturesResponse.data.data : []
              };
            } catch (pictureError) {
              return { ...post, images: [] };
            }
          })
        );

        setRelatedEvents(relatedWithPictures);
      }
    } catch (err) {
      console.error('Error fetching related events:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date TBA';
    try {
      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      };
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (error) {
      return 'Date TBA';
    }
  };

  const getEventImage = (event) => {
    if (event.images && event.images.length > 0) {
      const image = event.images[0];
      let imageUrl = image.picture_path || image.url || image.path;

      if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
        imageUrl = `/${imageUrl}`;
      }

      if (imageUrl && imageUrl.startsWith('/') && !imageUrl.startsWith('http')) {
        imageUrl = `http://192.168.100.100:8000${imageUrl}`;
      }

      return imageUrl;
    }
    return '/images/event-placeholder.jpg';
  };

  const getImageUrl = (image) => {
    let imageUrl = image.picture_path || image.url || image.path;

    if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('/')) {
      imageUrl = `/${imageUrl}`;
    }

    if (imageUrl && imageUrl.startsWith('/') && !imageUrl.startsWith('http')) {
      imageUrl = `http://192.168.100.100:8000${imageUrl}`;
    }

    return imageUrl;
  };

  const openImageModal = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (event.images && currentImageIndex < event.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
      setSelectedImage(event.images[currentImageIndex + 1]);
    }
  };

  const prevImage = () => {
    if (event.images && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
      setSelectedImage(event.images[currentImageIndex - 1]);
    }
  };

  // Simple HTML cleanup - just remove MS Word specific tags
  const cleanHtmlContent = (html) => {
    if (!html) return '';
    return html
      .replace(/class="Mso[^"]*"/gi, '')
      .replace(/<o:p>.*?<\/o:p>/gi, '')
      .replace(/<span[^>]*>/gi, '')
      .replace(/<\/span>/gi, '')
      .replace(/<font[^>]*>/gi, '')
      .replace(/<\/font>/gi, '');
  };

  const cleanedPostHtml = useMemo(() => {
    if (!event) return '';
    return cleanHtmlContent(event.post);
  }, [event?.post]);

  if (loading) {
    return (
      <section className="events-listing">
        <div className="container">
          <div className="support-section-title">
            <h2>Event Details</h2>
            <p>Loading event information...</p>
          </div>
          <div className="loading-container-test">
            <div className="spinner-test"></div>
            <p>Loading event details from ITAMBUE THAMANI YAKO...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || !event) {
    return (
      <section className="events-listing">
        <div className="container">
          <div className="support-section-title">
            <h2>Event Details</h2>
            <p className="error-text">{error}</p>
          </div>
          <div className="events-error">
            <button onClick={fetchEventDetails} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="event-details-container">
      <div>
        <br></br><br></br><br></br>
      </div>
      
      {/* Navigation */}
      <div className="event-navigation">
        <button onClick={() => navigate(-1)} className="nav-btn back">
          <FaArrowLeft /> Back to Events
        </button>
        <Link to="/events" className="nav-btn all-events">
          View All Events
        </Link>
      </div>

      {/* Event Content Layout */}
      <div className="event-content-layout">
        {/* Event Header with Hero Image */}
        <div className="event-header-with-hero">
          <div className="hero-image-container">
            {event.images && event.images.length > 0 ? (
              <div className="hero-image">
                <img
                  src={getImageUrl(event.images[0])}
                  alt={event.title}
                  onError={(e) => {
                    e.target.src = '/images/event-placeholder.jpg';
                  }}
                />
              </div>
            ) : (
              <div className="hero-image placeholder">
                <FaImages className="placeholder-icon" />
                <span>No Image Available</span>
              </div>
            )}

            <div className="event-header-overlay">
           
              <h1 className="event-title">{event.title}</h1>
            </div>
          </div>
        </div>

        {/* Event Content and Sidebar */}
        <div className="content-sidebar-wrapper">
          {/* Event Content */}
          <div className="event-content-section">
            <h3 className="content-title">About This Event</h3>
            <div 
              className="event-description"
              dangerouslySetInnerHTML={{ __html: cleanedPostHtml }}
            />
          </div>

          {/* Event Details Sidebar */}
          <div className="event-sidebar">
            <div className="event-info-card">
              <h4>Event Information</h4>
              <div className="info-item">
                <strong>Date & Time:</strong>
                <span>{formatDate(event.event_date)}</span>
              </div>
              {event.category && (
                <div className="info-item">
                  <strong>Category:</strong>
                  <span>{event.category}</span>
                </div>
              )}
              {event.location && (
                <div className="info-item">
                  <strong>Location:</strong>
                  <span><FaMapMarkerAlt style={{ marginRight: '5px' }} />{event.location}</span>
                </div>
              )}
              {event.featured === true && (
                <div className="info-item">
                  <strong>Status:</strong>
                  <span className="featured-status">Featured Event</span>
                </div>
              )}
              {event.images && event.images.length > 0 && (
                <div className="info-item">
                  <strong>Gallery:</strong>
                  <span>{event.images.length} photo{event.images.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Event Gallery - INCLUDING COVER IMAGE */}
        {event.images && event.images.length > 0 && (
          <div className="event-gallery-section">
            <div className="gallery-header">
              <h3 className="gallery-title">
                <FaImages className="gallery-icon" />
                Event Gallery
              </h3>
            </div>
            <div className="gallery-grid">
              {event.images.map((image, index) => (
                <div
                  key={image.id || index}
                  className="gallery-item"
                  onClick={() => openImageModal(image, index)}
                >
                  <div className="gallery-image-container">
                    <img
                      src={getImageUrl(image)}
                      alt={`${event.title} - Image ${index + 1}`}
                      onError={(e) => {
                        e.target.src = '/images/event-placeholder.jpg';
                      }}
                    />
                    <div className="gallery-overlay">
                      <FaImages className="overlay-icon" />
                      <span>View</span>
                    </div>
                  </div>
                  {image.caption && (
                    <p className="image-caption">{image.caption}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Related Events */}
      {relatedEvents.length > 0 && (
        <div className="related-events-section">
          <h3>Related Events</h3>
          <div className="related-events-grid">
            {relatedEvents.map(relatedEvent => (
              <div key={relatedEvent.id} className="related-event-card">
                <div className="related-event-image">
                  <img
                    src={getEventImage(relatedEvent)}
                    alt={relatedEvent.title}
                    onError={(e) => {
                      e.target.src = '/images/event-placeholder.jpg';
                    }}
                  />
                </div>
                <div className="related-event-content">
                  <h4>{relatedEvent.title}</h4>
                  <p className="related-event-date">
                    <FaCalendarAlt style={{ marginRight: '5px' }} />
                    {formatDate(relatedEvent.event_date)}
                  </p>
                  <Link
                    to={`/event-details/${relatedEvent.id}`}
                    className="related-event-link"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeImageModal}>
              <FaTimes />
            </button>

            <div className="modal-image-container">
              <img
                src={getImageUrl(selectedImage)}
                alt={`${event.title} - Image ${currentImageIndex + 1}`}
                onError={(e) => {
                  e.target.src = '/images/event-placeholder.jpg';
                }}
              />

              {selectedImage.caption && (
                <div className="modal-caption">
                  <p>{selectedImage.caption}</p>
                </div>
              )}
            </div>

            <div className="modal-navigation">
              <button
                className="nav-arrow prev-arrow"
                onClick={prevImage}
                disabled={currentImageIndex === 0}
              >
                <FaChevronLeft />
              </button>

              <div className="image-counter">
                {currentImageIndex + 1} / {event.images.length}
              </div>

              <button
                className="nav-arrow next-arrow"
                onClick={nextImage}
                disabled={currentImageIndex === event.images.length - 1}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;